import PdfLayout from '../PdfLayout';
import CustomTextWidget from './CustomTextWidget';
import moment from 'moment';
import { Utils } from '../../services';
import i18n from '../../locales/i18n';
import {
    PREVIEW_SCREENING_TITLE,
    pickPreviewSeat,
    previewLocalized,
    previewPlace,
} from '../../services/pdfLayoutPreviewSample';

/**
 * Class representing a PDF layout widget showing
 * an information about the screening.
 */
class ScreeningInfoWidget extends CustomTextWidget {

    constructor(properties) {
        super({
            ...(ScreeningInfoWidget.getDefaultOptions()),
            ...properties,
        });

        this.type    = PdfLayout.TEXT;
        this.subType = PdfLayout.SCREENING_INFO;

        if (['start_at', 'stop_at', 'start_at_stop_at'].includes(this.options?.field))
            this.options.date_format = this.options.date_format ?? 'lll';
    }

    /**
     * Get this widget default options
     */
    static getDefaultOptions() {
        return {
            position: { x: 2, y: 2, z: 0 },
            dimensions : { width: 5, height: 0.75 },
            transform: { rotation: 0 },
            options: {
                field: 'title',
                style: {
                    color: '#000000',
                    fontFamily: "Helvetica",
                    fontSize: 14,
                    lineHeight: 1,
                    textAlign: 'left',
                    textDecoration: 'none',
                    textStrokeWidth: 0,
                    textStrokeColor: '#000000',
                    backgroundColor: '#FFFFFF00',
                    borderStyle: 'solid',
                    borderWidth: 0,
                    borderColor: '#000000',
                    borderRadius: 0,
                    padding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                    }
                },
            }
        };
    }

    /**
     * Return the list of options this widget handles
     */
    getHandledOptions() {
        const handledOptions = super.getHandledOptions().concat([
            PdfLayout.OPTION_SCREENING_FIELD
        ]).filter(o => o !== PdfLayout.OPTION_TEXT);

        if (['start_at', 'stop_at', 'start_at_stop_at'].includes(this.options?.field)) {
            handledOptions.push(PdfLayout.OPTION_DATE_FORMAT);
            handledOptions.push(PdfLayout.OPTION_DATE_OFFSET);
        }

        return handledOptions;
    }

    /**
     * Return this widget preview text as string
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {Object} setting
     */
    getPreviewText(props) {
        const oneTimePass = "one-time-pass";
        const { programmation, place, tickettype, inEdition } = props;

        // Displays only screening information when editing, simulating OTP,
        // or when a programmation context is available (template preview).
        if (!inEdition && tickettype && oneTimePass !== tickettype._id && !programmation)
            return "";

        const momentFormat = this.options?.date_format ?? 'lll';
        const lang = props.lang || 'fr';
        const samplePlace = previewPlace(lang);
        const sampleTitle = previewLocalized(PREVIEW_SCREENING_TITLE, lang);
        // Match Engine PdfLayout moment.locale (fr → fr-ch, …) for L / lll separators.
        const fmt = (value) => moment(value).locale(Utils.pdfMomentLocale(lang)).format(momentFormat);
        // Same instant as Engine previewPdf buildDefaultPreviewScreening.
        const sampleStart = moment('1970-12-01T20:00:00');

        switch (this.options.field) {
            case 'title':
                return  Utils.localized_or_fallback(programmation?.title, lang, sampleTitle);
            case 'venue':
                return place?.cinema ?? samplePlace.cinema;
            case 'place':
                return place?.name ?? samplePlace.name;
            case 'address':
                return place?.address ?? samplePlace.address;
            case 'zip':
                return place?.zip ?? samplePlace.zip;
            case 'city':
                return place?.city ?? samplePlace.city;
            case 'state':
                return place?.state ?? samplePlace.state ?? '';
            case 'country':
                return place?.country ?? samplePlace.country;
            case 'start_at':
                // For NIFFF https://yoda.netoxygen.ch/projects/4b151b2c-6270-44e3-a92d-56c227854844/change/456500a0-8320-4239-a991-a751b001f883
                if (this.options?.date_format === "midnight_hack") {
                    return PdfLayout.midnightHack(programmation?.start_at, i18n.t("layouts.midnight"), i18n.t("layouts.midnight15"), i18n.t("layouts.midnight30"));
                } else {
                    if (programmation?.start_at && programmation?.times?.length > 0) {
                        const startDate = moment(programmation.start_at).format('YYYY-MM-DD');
                        const firstTime = programmation.times.sort()[0];

                        return fmt(`${startDate} ${firstTime}`);
                    }
                    if (programmation?.start_at)
                        return fmt(programmation.start_at);
                    return fmt(sampleStart);
                }
            case 'stop_at':
                return programmation?.stop_at ? fmt(programmation.stop_at) : fmt(sampleStart.clone().add(2, 'hours').add(30, 'minutes'));
            case 'start_at_stop_at':
                return [
                    programmation?.start_at ? fmt(programmation.start_at) : fmt(sampleStart),
                    programmation?.stop_at ? fmt(programmation.stop_at) : fmt(sampleStart.clone().add(2, 'hours').add(30, 'minutes')),
                ].join(' - ');
            case 'seat_zone':
            case 'seat_row':
            case 'seat_number': {
                const seat = pickPreviewSeat(programmation || place);
                const parts = seat.split(':');
                if (this.options.field === 'seat_zone')
                    return parts[0] || '';
                if (this.options.field === 'seat_row')
                    return parts[1] || '';
                return parts[2] || '';
            }
            default:
                return '...';
        }
    }

    /**
     * Return the icon to use on the editor button
     */
    getIcon() {
        return 'film';
    }
}

export default ScreeningInfoWidget;
